import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { LocalStorageService } from '../../configuration';
import type { RootState } from '../../configuration';
import {
  applyToBrandListing,
  claimBrief,
  getBrandCreatorSetting,
  setBrandCreatorSetting,
} from '../../api/payment/brand-deal.api';
import type {
  BrandBriefFeedItem,
  BrandCreatorSetting,
  OrdererKind,
} from '../../api/payment/brand-deal.api';
import type { PublicBrandMarketItem } from '../../api/payment/brand-public-market.api';
import BrandProposalComposer from '../../components/brand-deal/brand-proposal-composer';
import { useBrandTaxConsent } from '../../hooks/use-brand-tax-consent';
import { routePaths } from '../../routes/routes-path';
import RequestUnlockDonate from '../../components/request-unlock-donate/request-unlock-donate.component';
import { EkycCameraModal } from '../donate-goal/ekyc-camera-modal';

/**
 * Nút nhận việc ở trang chi tiết chiến dịch công khai. Khách thì mời đăng nhập (nhớ trang để quay lại). Tài khoản chưa đủ điều kiện
 * thì có nút làm ngay bước còn thiếu: đăng ký làm creator, xác thực eKYC hoặc bật nhận deal. Đủ điều kiện thì nhận đúng kiểu
 * của chiến dịch (xin tham gia / nhận brief / gửi đề xuất).
 */

/** Bước còn thiếu trước khi nhận việc; null = đủ điều kiện (hoặc chưa biết, để máy chủ tự báo lỗi khi bấm). */
export type MissingStep =
  | 'creator'
  | 'creatorPending'
  | 'ekyc'
  | 'acceptDeals'
  | null;

export const missingStep = (
  setting: BrandCreatorSetting | null,
  requestPending: boolean
): MissingStep => {
  if (!setting || setting.isCreator === undefined) return null;
  if (!setting.isCreator) return requestPending ? 'creatorPending' : 'creator';
  if (!setting.isEkycVerified) return 'ekyc';
  if (!setting.acceptDeals) return 'acceptDeals';
  return null;
};

const gateBox =
  'flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300';

export const MESSAGE_MAX_LENGTH = 300;

const errorMessage = (err: unknown) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data
    ?.message || 'Không thực hiện được, thử lại sau.';

/** Hộp gửi đề xuất dùng kiểu thẻ brief của creator; trường sàn công khai không có thì điền mặc định. */
const toBriefFeedItem = (item: PublicBrandMarketItem): BrandBriefFeedItem => ({
  ordererKind: item.ordererKind as OrdererKind,
  contentFormat: item.contentFormat,
  campaignId: item.campaignId,
  brandName: item.brandName,
  title: item.title,
  briefText: item.summary,
  submitDays: item.submitDays ?? 0,
  maxRevisions: item.maxRevisions,
  images: item.images,
  slotPriceVnd: item.slotPriceVnd,
  netVnd: item.netVnd,
  slotsLeft: item.slotsLeft,
  slotsTotal: item.slotsTotal,
  applyDeadline: item.applyDeadline,
  minFollowers: item.minFollowers,
  categories: item.categories,
  eligible: true,
  ineligibleReason: null,
  businessRatingAverage: item.businessRatingAverage,
  businessRatingCount: item.businessRatingCount,
  adAccountId: item.adAccountId,
  kind: 'Proposal',
  myProposalStatus: null,
});

const primaryBtn =
  'w-full rounded-lg bg-[#0057FF] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60';

const CampaignAction: React.FC<{ item: PublicBrandMarketItem }> = ({
  item,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useSelector((s: RootState) => s.features.auth.profile);
  const isLogged = !!LocalStorageService.getItem('accessToken');
  const taxConsent = useBrandTaxConsent();

  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(false);
  const [proposalSent, setProposalSent] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const inFlight = useRef(false);
  const [setting, setSetting] = useState<BrandCreatorSetting | null>(null);
  const [settingLoaded, setSettingLoaded] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [ekycOpen, setEkycOpen] = useState(false);
  const [enabling, setEnabling] = useState(false);

  const loadSetting = () =>
    getBrandCreatorSetting()
      .then(setSetting)
      .catch(() => setSetting(null))
      .finally(() => setSettingLoaded(true));

  useEffect(() => {
    if (isLogged) loadSetting();
    else setSettingLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogged]);

  if (!isLogged) {
    return (
      <button
        type="button"
        className={primaryBtn}
        onClick={() => {
          try {
            sessionStorage.setItem(
              'postLoginRedirect',
              location.pathname + location.search
            );
          } catch {
            /* trình duyệt chặn sessionStorage thì thôi, đăng nhập xong về trang chủ */
          }
          navigate(routePaths.login);
        }}
      >
        Đăng nhập để nhận việc
      </button>
    );
  }

  if (!settingLoaded) {
    return <div className="ui-muted text-[13px]">Đang kiểm tra tài khoản…</div>;
  }

  const step = missingStep(
    setting,
    requestSent || !!profile?.isCreateRequestReciveDonate
  );

  if (step === 'creator') {
    return (
      <div className={gateBox}>
        <p>
          Cần là creator mới nhận được việc. Gửi yêu cầu, admin duyệt xong là
          bạn nhận việc được.
        </p>
        <RequestUnlockDonate
          label="Đăng ký làm creator"
          onSubmitted={() => setRequestSent(true)}
        />
      </div>
    );
  }

  if (step === 'creatorPending') {
    return (
      <div className={gateBox}>
        <p>
          Yêu cầu làm creator của bạn đang chờ admin duyệt. Duyệt xong bạn quay
          lại đây nhận việc.
        </p>
      </div>
    );
  }

  if (step === 'ekyc') {
    return (
      <div className={gateBox}>
        <p>
          Bạn cần xác thực danh tính bằng CCCD trước khi nhận việc, chỉ mất
          khoảng một phút.
        </p>
        <button
          type="button"
          className={primaryBtn}
          onClick={() => setEkycOpen(true)}
        >
          Xác thực eKYC
        </button>
        <EkycCameraModal
          open={ekycOpen}
          identityOnly
          onClose={() => setEkycOpen(false)}
          onVerified={() => {
            setEkycOpen(false);
            loadSetting();
          }}
        />
      </div>
    );
  }

  if (step === 'acceptDeals') {
    const enable = () => {
      if (enabling) return;
      setEnabling(true);
      setBrandCreatorSetting(true)
        .then((s) => {
          setSetting((prev) => ({
            ...(prev ?? s),
            ...s,
            isCreator: true,
            isEkycVerified: true,
          }));
          toast.success('Đã bật nhận deal quảng cáo.');
        })
        .catch((err) => toast.error(errorMessage(err)))
        .finally(() => setEnabling(false));
    };
    return (
      <div className={gateBox}>
        <p>Bạn chưa bật nhận deal quảng cáo. Bật lên là nhận việc được ngay.</p>
        <button
          type="button"
          className={primaryBtn}
          disabled={enabling}
          onClick={enable}
        >
          {enabling ? 'Đang bật…' : 'Bật nhận deal'}
        </button>
      </div>
    );
  }

  const run = async (action: () => Promise<void>, retry: () => void) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      await action();
    } catch (err) {
      if (!taxConsent.handle(err, retry)) toast.error(errorMessage(err));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const apply = () =>
    run(
      async () => {
        await applyToBrandListing(item.campaignId, message.trim() || undefined);
        setApplied(true);
        toast.success('Đã gửi đơn, chờ doanh nghiệp duyệt.');
      },
      () => apply()
    );

  const claim = () =>
    run(
      async () => {
        const task = await claimBrief(item.campaignId);
        navigate(`${routePaths.brandDeals}/task/${task.dealId}`);
      },
      () => claim()
    );

  let body: React.ReactNode = null;
  if (item.kind === 'Direct') {
    body = (
      <div className="flex flex-col gap-2">
        <textarea
          value={message}
          maxLength={MESSAGE_MAX_LENGTH}
          disabled={applied}
          onChange={(e) =>
            setMessage(e.target.value.slice(0, MESSAGE_MAX_LENGTH))
          }
          rows={3}
          placeholder="Lời nhắn cho doanh nghiệp (không bắt buộc)"
          className="w-full rounded-lg border ui-divider bg-transparent p-2 text-[14px] outline-none ui-text"
        />
        <span className="ui-muted self-end text-[12px]">
          {message.length}/{MESSAGE_MAX_LENGTH}
        </span>
        <button
          type="button"
          className={primaryBtn}
          disabled={busy || applied}
          onClick={apply}
        >
          {applied ? 'Đã gửi đơn' : busy ? 'Đang gửi…' : 'Xin tham gia'}
        </button>
      </div>
    );
  } else if (item.kind === 'Creative') {
    body = (
      <button
        type="button"
        className={primaryBtn}
        disabled={busy}
        onClick={() => {
          if (inFlight.current) return;
          const question =
            item.submitDays != null
              ? `Nhận brief này? Bạn có ${item.submitDays} ngày để nộp bài.`
              : 'Nhận brief này?';
          if (!window.confirm(question)) return;
          claim();
        }}
      >
        {busy ? 'Đang nhận…' : 'Nhận làm'}
      </button>
    );
  } else {
    body = (
      <button
        type="button"
        className={primaryBtn}
        disabled={proposalSent}
        onClick={() => setComposerOpen(true)}
      >
        {proposalSent ? 'Đã gửi đề xuất' : 'Gửi đề xuất'}
      </button>
    );
  }

  return (
    <>
      {body}
      {composerOpen && (
        <BrandProposalComposer
          item={toBriefFeedItem(item)}
          onClose={() => setComposerOpen(false)}
          onSent={() => {
            setComposerOpen(false);
            setProposalSent(true);
          }}
          handleError={(err, retry) => taxConsent.handle(err, retry)}
        />
      )}
      {taxConsent.dialog}
    </>
  );
};

export default CampaignAction;
